import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    createTheme,
    ThemeProvider,
    Alert,
} from '@mui/material';

import lumaLogo from '../../assets/luma-logo.png';
import Toast from '../../components/utils/Toast';

const theme = createTheme({
    palette: {
        primary: {
            main: '#A86E64',
        },
        background: {
            default: '#ECEAE5',
            paper: 'transparent',
        },
    },
});

const ForgotPassword: React.FC = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { requestPasswordReset } = useAuth();
    const navigate = useNavigate();
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email) {
            setToastMessage(t('forgotPassword.errorEnterEmail'));
            setToastSeverity('error');
            setToastOpen(true);
            return;
        }

        try {
            await requestPasswordReset(email);
            setToastMessage(t('forgotPassword.successResetSent'));
            setToastSeverity('success');
            setToastOpen(true);

        } catch (err) {
            setToastMessage(t('forgotPassword.errorResetFailed'));
            setToastSeverity('error');
            setToastOpen(true);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                <Container maxWidth="xs" sx={{ display: 'flex', alignItems: 'center', pt: 4, pb: 4 }}>
                    <Card sx={{ width: '100%', boxShadow: 0, bgcolor: 'background.paper' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="center" mb={2}>
                                <img
                                    src={lumaLogo}
                                    alt="Luma Logo"
                                    style={{ maxWidth: 80, height: 'auto' }}
                                />
                            </Box>

                            <Typography variant="h5" component="h1" textAlign="center" gutterBottom>
                                {t('forgotPassword.title')}
                            </Typography>

                            <Typography variant="body2" textAlign="center" color="textSecondary" sx={{ mb: 3 }}>
                                {t('forgotPassword.description')}
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    {success}
                                </Alert>
                            )}

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label={t('forgotPassword.emailLabel')}
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />

                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ mt: 2, mb: 2 }}
                                onClick={handleSubmit}
                            >
                                {t('forgotPassword.resetButton')}
                            </Button>

                            <Box mt={2} textAlign="center">
                                <Typography variant="body2" color="textSecondary">
                                    {t('forgotPassword.rememberPassword')}{' '}
                                    <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                                        {t('forgotPassword.signIn')}
                                    </Link>
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Container>
                <Toast
                    open={toastOpen}
                    message={toastMessage}
                    severity={toastSeverity}
                    onClose={() => setToastOpen(false)}
                />
            </Box>
        </ThemeProvider>
    );
};

export default ForgotPassword;
